/**
 * Note: If deleting this file please also delete the exceptions in this file:
 * `packages/editor/generative-ai-modal/src/ui/screens/Preview/__tests__/vr-tests/Preview.vr.ts`
 * ```ts
 * a11y: {
 *	skip: true,
 * },
 * ```
 *
 * These exceptions were made because we have nested buttons inside the EditPrompt button which is a violation.
 * However, we expect these will be resolved with the completion of this work:
 * https://home.atlassian.com/o/2346a038-3c8c-498b-a79b-e7847859868d/s/a436116f-02ce-4520-8fbb-7301462a1674/project/ATLAS-96926/updates
 */

/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/** @jsxFrag */
import React from 'react';

import { css as cssUnbounded } from '@compiled/react';
import { useIntl } from 'react-intl';

import { cssMap, css, jsx } from '@atlaskit/css';
import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import type { Schema } from '@atlaskit/editor-prosemirror/model';
import { Box, Flex, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { EditorAgent } from '../../../utils/agents';
import { useAgentRefinementTagProps } from '../../hooks/use-agent-refinement-tag-props';
import { BackIconButton } from '../BackIconButton/BackIconButton';
import { ClosePreviewIconButton } from '../ClosePreviewIconButton/ClosePreviewIconButton';
import { EditPromptHoverTag } from '../EditPromptHoverTag/EditPromptHoverTag';
import { LatestPromptRenderer } from '../LatestPromptRenderer/LatestPromptRenderer';
import { RefinementTag } from '../RefinementTag/RefinementTag';

import { messages } from './messages';

const PROMPT_CONTENT_ID = 'ai-latest-prompt-content';

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const headerEditStylesUnbounded = cssUnbounded({
	color: 'inherit',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: 'unset',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: 'inherit',
	borderBottomLeftRadius: 0,
	borderBottomRightRadius: 0,
	'&:hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'&:hover #edit-prompt-hover-button': {
		opacity: 1,
	},
	'&:active': {
		backgroundColor: token('color.background.neutral.subtle.pressed'),
	},
	'&:focus': {
		outline: `${token('border.width.focused')} solid ${token('color.border.focused')}`,
		outlineOffset: token('space.negative.025'),
	},
});
const headerEditStyles = css({
	cursor: 'pointer',
	border: 'none',
	width: '100%',
	backgroundColor: 'transparent',
	textAlign: 'start',
	borderRadius: token('radius.large'),
});

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const contentContainerStylesUnbounded = cssUnbounded({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '20px',
});

const contentContainerStyles = css({
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	hyphens: 'auto',
	wordWrap: 'break-word',
});

const fullWidth = cssMap({
	default: {
		width: '100%',
	},
});

const titleContentWrapper = cssMap({
	default: {
		flexGrow: 1,
		// https://css-tricks.com/flexbox-truncated-text/
		minWidth: '0px',
	},
});

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const headerStylesUnbounded = cssUnbounded({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'div p': {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '20px',
	},
});

const headerStyles = css({
	paddingTop: token('space.150'),
	paddingBottom: token('space.150'),
	paddingLeft: token('space.150'),
	paddingRight: token('space.150'),
	overflowWrap: 'break-word',
	overflow: 'hidden',
	width: '100%',
});

// header has width/padding at the same time
// if there is very long prompt, header will be wider than parent
// so it should be border-box
const headerBoxSizingStyles = css({
	boxSizing: 'border-box',
});

type Props = {
	agent?: EditorAgent;
	/**
	 * Short title for the submenu if the user selects a submenu via a parent (`Change tone` -> `Neutral`)
	 * If the user selects a submenu directly, this will be empty (search and select `Change tone to Neutral`)
	 */
	childShortTitle?: string;
	isClearTitle?: boolean;
	onBack?: () => unknown;
	onClose?: () => void;
	onEditPromptClick?: () => void;
	/**
	 * Label for the parent if the user selects a submenu via a parent (`Change tone` -> `Neutral`)
	 * If the user selects a submenu directly, this will be empty (search and select `Change tone to Neutral`)
	 */
	parentPresetPromptLabel?: string;
	presetTitle?: string;
	prompt?: string;
	promptADF?: JSONDocNode;
	providerFactory: ProviderFactory;
	schema: Schema;
	showBack?: boolean;
};

export const LatestPromptHeader = ({
	prompt,
	promptADF,
	presetTitle,
	providerFactory,
	onEditPromptClick,
	isClearTitle,
	agent,
	showBack,
	onBack,
	onClose,
}: Props): JSX.Element | null => {
	const { formatMessage } = useIntl();

	// when used in Loading screen, there is no onEditPromptClick
	const canEdit = !!onEditPromptClick;

	const promptContent = React.useMemo(() => {
		if (promptADF) {
			return (
				<Flex alignItems="center">
					<LatestPromptRenderer promptADF={promptADF} providerFactory={providerFactory} />
					{!presetTitle && (
						<EditPromptHoverTag isClearMode={!presetTitle && !prompt} isPlaceholder={!canEdit} />
					)}
				</Flex>
			);
		}

		if (prompt) {
			return <div css={contentContainerStyles}>{prompt}</div>;
		}

		return null;
	}, [canEdit, prompt, promptADF, providerFactory, presetTitle]);

	const refinementTagProps = useAgentRefinementTagProps(agent);

	const header = React.useMemo(() => {
		return (
			<Flex>
				{showBack && (
					<BackIconButton inPromptHeader isDisabled={!canEdit} stopPropagation onClick={onBack} />
				)}
				<Flex direction="column" gap="space.050" xcss={titleContentWrapper.default}>
					{presetTitle && (
						<Flex alignItems="center">
							<div>
								{isClearTitle ? (
									<Text weight={'semibold'}>{presetTitle}</Text>
								) : (
									<RefinementTag
										label={presetTitle}
										// Ignored via go/ees005
										// eslint-disable-next-line react/jsx-props-no-spreading
										{...refinementTagProps}
										onClick={
											canEdit
												? () => {
														if (onBack) {
															onBack();
														}
													}
												: undefined
										}
									/>
								)}
							</div>
							{presetTitle && (
								<EditPromptHoverTag
									isClearMode={!!presetTitle && !prompt}
									isPlaceholder={!canEdit}
								/>
							)}
						</Flex>
					)}
					{promptContent}
				</Flex>
				{!!onClose && <ClosePreviewIconButton onClick={onClose} />}
			</Flex>
		);
	}, [
		showBack,
		canEdit,
		onBack,
		presetTitle,
		isClearTitle,
		refinementTagProps,
		prompt,
		promptContent,
		onClose,
	]);

	if (!(prompt || presetTitle)) {
		return null;
	}

	return (
		<Box testId="prompt-header" xcss={fullWidth.default}>
			{!canEdit ? (
				<div
					css={[
						headerStyles,
						headerStylesUnbounded,
						headerBoxSizingStyles,
						contentContainerStyles,
						contentContainerStylesUnbounded,
					]}
					id={PROMPT_CONTENT_ID}
					data-testid={PROMPT_CONTENT_ID}
				>
					{header}
				</div>
			) : (
				// eslint-disable-next-line @atlaskit/design-system/no-html-button
				<button
					type="button"
					data-testid="edit-prompt-button"
					onClick={onEditPromptClick}
					css={[
						headerStyles,
						headerStylesUnbounded,
						headerBoxSizingStyles,
						contentContainerStyles,
						contentContainerStylesUnbounded,
						headerEditStyles,
						headerEditStylesUnbounded,
					]}
					aria-label={formatMessage(messages.editPrompt)}
					aria-describedby={PROMPT_CONTENT_ID}
				>
					<div id={PROMPT_CONTENT_ID} data-testid={PROMPT_CONTENT_ID}>
						{header}
					</div>
				</button>
			)}
		</Box>
	);
};
